import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type {
  DataGap,
  DataGapSeverity,
  PlanningModel,
} from '@/features/manufacturing/planning/types';
import { cn } from '@/lib/utils';
import { GAP_SEVERITY_ICON, GAP_SEVERITY_LABEL, GAP_SEVERITY_VARIANT } from './shared';

interface MissingInfoTabProps {
  model: PlanningModel;
  onEditPlanLine: (planLineId: string) => void;
  onOpenSettings: () => void;
}

const SEVERITY_ORDER: DataGapSeverity[] = ['blocking', 'warning', 'info'];

const SEVERITY_INTRO: Record<DataGapSeverity, string> = {
  blocking: 'The plan cannot answer "when do I order" for these until they are filled in.',
  warning: 'The plan runs, but these make its dates or cash figures less trustworthy.',
  info: 'Worth knowing; nothing to do right now.',
};

function FixLink({
  gap,
  onEditPlanLine,
  onOpenSettings,
}: { gap: DataGap } & Pick<MissingInfoTabProps, 'onEditPlanLine' | 'onOpenSettings'>) {
  const fix = gap.fix;
  if (!fix) return null;
  const cls = 'inline-flex items-center gap-1 text-xs text-info hover:underline';
  switch (fix.type) {
    case 'item': {
      const tab = fix.tab === 'bom' ? null : fix.tab === 'suppliers' ? 'suppliers' : 'details';
      const to =
        fix.tab === 'bom'
          ? `/mfg/boms/${fix.itemId}`
          : `/catalog/items/${fix.itemId}${tab ? `?tab=${tab}` : ''}`;
      return (
        <Link to={to} className={cls}>
          {fix.tab === 'bom'
            ? 'Open BOM builder'
            : fix.tab === 'suppliers'
              ? 'Edit suppliers'
              : 'Open item'}
          <ArrowUpRight className="h-3 w-3" />
        </Link>
      );
    }
    case 'po':
      return (
        <Link to="/procurement/purchase-orders" className={cls}>
          Open purchase orders <ArrowUpRight className="h-3 w-3" />
        </Link>
      );
    case 'plan_line':
      return (
        <button type="button" className={cls} onClick={() => onEditPlanLine(fix.planLineId)}>
          Edit target <ArrowUpRight className="h-3 w-3" />
        </button>
      );
    case 'settings':
      return (
        <button type="button" className={cls} onClick={onOpenSettings}>
          Open settings <ArrowUpRight className="h-3 w-3" />
        </button>
      );
  }
}

/**
 * Everything the planner is missing to give a straight answer, grouped
 * by how much it matters, each with a link to where it gets fixed.
 */
export function MissingInfoTab({ model, onEditPlanLine, onOpenSettings }: MissingInfoTabProps) {
  const grouped = useMemo(() => {
    const out = new Map<DataGapSeverity, DataGap[]>();
    for (const gap of model.gaps) {
      const list = out.get(gap.severity) ?? [];
      list.push(gap);
      out.set(gap.severity, list);
    }
    return out;
  }, [model.gaps]);

  if (model.gaps.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <CheckCircle2 className="h-8 w-8 text-bright-moss-dark" />
          <div>
            <div className="font-medium">Nothing missing</div>
            <p className="mt-1 text-sm text-muted-foreground">
              Every part in the plan has a BOM, a supplier, a lead time and a cost.
            </p>
          </div>
          {model.schedules.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Add a production target and the planner will check again.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {SEVERITY_ORDER.map((severity) => {
        const gaps = grouped.get(severity);
        if (!gaps || gaps.length === 0) return null;
        const Icon = GAP_SEVERITY_ICON[severity];
        return (
          <Card key={severity}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Icon
                  className={cn(
                    'h-4 w-4',
                    severity === 'blocking'
                      ? 'text-fresh-salmon-dark'
                      : severity === 'warning'
                        ? 'text-fresh-salmon'
                        : 'text-muted-foreground',
                  )}
                />
                {GAP_SEVERITY_LABEL[severity]}
                <Badge variant={GAP_SEVERITY_VARIANT[severity]}>{gaps.length}</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">{SEVERITY_INTRO[severity]}</p>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-border">
                {gaps.map((gap) => (
                  <li
                    key={gap.id}
                    className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{gap.title}</div>
                      <p className="text-xs text-muted-foreground">{gap.detail}</p>
                    </div>
                    <div className="shrink-0">
                      <FixLink
                        gap={gap}
                        onEditPlanLine={onEditPlanLine}
                        onOpenSettings={onOpenSettings}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}
      {model.settings.safetyStockDays === 0 && model.purchased.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Tip: safety stock is 0 days, so orders are timed to land exactly as stock hits zero.{' '}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={onOpenSettings}
          >
            Add a few days of cushion
          </Button>
          .
        </p>
      )}
    </div>
  );
}
